import { SyncAuditEntry } from '@/domain/sync-audit'
import { CommonModule } from '@angular/common'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { ButtonModule } from 'primeng/button'
import { SkeletonModule } from 'primeng/skeleton'
import { AtomicDelivery, AtomicDeliveryStatus, SyncAuditService } from '@/service/sync-audit.service'

type SyncAuditMetricKey =
  | 'aws_repayments_count'
  | 'sf_repayments_count'
  | 'aws_repayments_sum'
  | 'sf_repayments_sum'
  | 'aws_repayments_pending_sync_count'
  | 'aws_repayments_pending_sync_sum'
  | 'aws_repayments_pending_sync_delete_count'
  | 'aws_merchants_total_current_outstanding_sum'
  | 'sf_merchants_total_current_outstanding_sum'
  | 'aws_merchants_pending_sync_count'
  | 'aws_cash_advances_count'
  | 'sf_cash_advances_count'
  | 'aws_terminals_count'
  | 'sf_terminals_count'

type SyncAuditMetricFormat = 'currency' | 'number'

interface BaselineMetric {
  awsId: SyncAuditMetricKey
  salesforceId: SyncAuditMetricKey
  format: SyncAuditMetricFormat
  aws: SyncAuditEntry | null
  salesforce: SyncAuditEntry | null
}

interface PendingMetric {
  id: SyncAuditMetricKey
  format: SyncAuditMetricFormat
  entry: SyncAuditEntry | null
  isDeletion?: boolean
}

interface SyncAuditEntityGroup {
  title: string
  icon: string
  accent: string
  baseline: BaselineMetric[]
  pending: PendingMetric[]
}

interface SalesforceQuarantineRow extends AtomicDelivery {
  terminalDisplay: string
  retrying: boolean
  result: 'succeeded' | 'failed' | null
}

@Component({
  selector: 'sync-audit',
  standalone: true,
  templateUrl: './sync-audit.component.html',
  styleUrl: './sync-audit.component.scss',
  imports: [CommonModule, SkeletonModule, ButtonModule],
})
export class SyncAuditComponent implements OnInit, OnDestroy {
  loading = true
  error: string | null = null
  private destroyed = false
  private readonly lifecycleAbort = new AbortController()
  private auditEntries = new Map<string, SyncAuditEntry>()
  private quarantineRefreshTimer: ReturnType<typeof setInterval> | null = null
  private readonly retryTimers = new Map<string, ReturnType<typeof setInterval>>()

  quarantinesLoading = true
  quarantinesError: string | null = null
  quarantines: SalesforceQuarantineRow[] = []

  groups: SyncAuditEntityGroup[] = [
    {
      title: 'Repayments',
      icon: 'pi pi-chart-line',
      accent: 'emerald',
      baseline: [
        {
          awsId: 'aws_repayments_count',
          salesforceId: 'sf_repayments_count',
          format: 'number',
          aws: null,
          salesforce: null,
        },
        {
          awsId: 'aws_repayments_sum',
          salesforceId: 'sf_repayments_sum',
          format: 'currency',
          aws: null,
          salesforce: null,
        },
      ],
      pending: [
        {
          id: 'aws_repayments_pending_sync_count',
          format: 'number',
          entry: null,
        },
        {
          id: 'aws_repayments_pending_sync_sum',
          format: 'currency',
          entry: null,
        },
        {
          id: 'aws_repayments_pending_sync_delete_count',
          format: 'number',
          entry: null,
          isDeletion: true,
        },
      ],
    },
    {
      title: 'Merchants',
      icon: 'pi pi-shop',
      accent: 'violet',
      baseline: [
        {
          awsId: 'aws_merchants_total_current_outstanding_sum',
          salesforceId: 'sf_merchants_total_current_outstanding_sum',
          format: 'currency',
          aws: null,
          salesforce: null,
        },
      ],
      pending: [
        {
          id: 'aws_merchants_pending_sync_count',
          format: 'number',
          entry: null,
        },
      ],
    },
    {
      title: 'Cash Advances',
      icon: 'pi pi-wallet',
      accent: 'amber',
      baseline: [
        {
          awsId: 'aws_cash_advances_count',
          salesforceId: 'sf_cash_advances_count',
          format: 'number',
          aws: null,
          salesforce: null,
        },
      ],
      pending: [],
    },
    {
      title: 'Terminals',
      icon: 'pi pi-credit-card',
      accent: 'rose',
      baseline: [
        {
          awsId: 'aws_terminals_count',
          salesforceId: 'sf_terminals_count',
          format: 'number',
          aws: null,
          salesforce: null,
        },
      ],
      pending: [],
    },
  ]

  private readonly metricIds = this.groups.flatMap((group) => [
    ...group.baseline.flatMap((metric) => [metric.awsId, metric.salesforceId]),
    ...group.pending.map((metric) => metric.id),
  ])

  constructor(private readonly syncAuditService: SyncAuditService) {}

  ngOnInit(): void {
    void this.loadSyncAudit()
  }

  ngOnDestroy(): void {
    this.destroyed = true
    this.lifecycleAbort.abort()
    if (this.quarantineRefreshTimer) clearInterval(this.quarantineRefreshTimer)
    this.retryTimers.forEach((timer) => clearInterval(timer))
    this.retryTimers.clear()
    this.syncAuditService.cancelAllRequests()
  }

  private async loadSyncAudit(): Promise<void> {
    this.loading = true
    this.error = null

    try {
      const entries = await this.syncAuditService.loadSyncAuditEntries(
        this.metricIds,
        { signal: this.lifecycleAbort.signal },
      )
      if (this.destroyed) return

      this.auditEntries = new Map(entries.map((entry) => [entry.id, entry]))
      this.syncMetricValues()
    } catch (error) {
      if (this.destroyed) return
      this.error = error instanceof Error ? error.message : 'Failed to load sync audit.'
    } finally {
      if (!this.destroyed) this.loading = false
    }
  }

  retry(): void {
    if (!this.loading) void this.loadSyncAudit()
  }

  async retryQuarantine(row: SalesforceQuarantineRow): Promise<void> {
    if (row.status !== 'QUARANTINED' || row.retrying) return
    if (!window.confirm(`Retry Salesforce delivery ${row.deliveryId}?`)) return

    this.updateQuarantine({ ...row, retrying: true, result: null })
    try {
      const updated = await this.syncAuditService.retryAtomicDelivery(row.merchantId, row.deliveryId, {
        signal: this.lifecycleAbort.signal,
      })
      if (this.destroyed) return
      this.updateQuarantine(this.toQuarantineRow(updated, true, null))
      this.pollRetriedQuarantine(updated.merchantId, updated.deliveryId)
    } catch (error) {
      if (this.destroyed) return
      this.quarantinesError = error instanceof Error ? error.message : 'Failed to retry delivery.'
      this.updateQuarantine({ ...row, retrying: false })
    }
  }

  quarantineStatusLabel(status: AtomicDeliveryStatus): string {
    if (status === 'QUARANTINED') return 'Quarantined'
    if (status === 'ACKNOWLEDGED') return 'Resolved'
    return 'Retrying'
  }

  quarantineStatusClass(status: AtomicDeliveryStatus): string {
    if (status === 'QUARANTINED') return 'error'
    if (status === 'ACKNOWLEDGED') return 'resolved'
    return 'retrying'
  }

  formatDate(value: string | null | undefined): string {
    if (!value) return '—'
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? '—' : new Intl.DateTimeFormat('en-GB', {
      dateStyle: 'medium', timeStyle: 'medium',
    }).format(date)
  }

  sanitizedLastError(value: string | null | undefined): string {
    if (!value) return '—'
    return value
      .replace(/(bearer\s+)[\w.-]+/gi, '$1[redacted]')
      .replace(/(authorization|token|password|secret)\s*[:=]\s*[^\s,;]+/gi, '$1=[redacted]')
      .replace(/[\r\n]+/g, ' ')
      .trim()
      .slice(0, 500) || '—'
  }

  async loadQuarantines(): Promise<void> {
    this.quarantinesLoading = this.quarantines.length === 0
    this.quarantinesError = null
    try {
      const [summaryResult, liveResult] = await Promise.allSettled([
        this.syncAuditService.loadSalesforceQuarantines({ signal: this.lifecycleAbort.signal }),
        this.syncAuditService.listAtomicDeliveries({ signal: this.lifecycleAbort.signal }),
      ])
      if (this.destroyed) return
      if (liveResult.status === 'rejected') throw liveResult.reason
      const summaryRows = summaryResult.status === 'fulfilled' ? summaryResult.value : []
      const liveRows = liveResult.value
      const rows = new Map<string, SalesforceQuarantineRow>()
      summaryRows.forEach((entry) => {
        const row = this.summaryToQuarantineRow(entry)
        if (row) rows.set(this.quarantineKey(row), row)
      })
      liveRows.forEach((row) => rows.set(this.quarantineKey(row), this.toQuarantineRow(row)))
      this.quarantines.filter((row) => row.retrying || row.status === 'ACKNOWLEDGED').forEach((row) => {
        if (!rows.has(this.quarantineKey(row))) rows.set(this.quarantineKey(row), row)
      })
      this.quarantines = Array.from(rows.values())
    } catch (error) {
      if (!this.destroyed) this.quarantinesError = error instanceof Error ? error.message : 'Failed to load Salesforce quarantines.'
    } finally {
      if (!this.destroyed) this.quarantinesLoading = false
    }
  }

  private pollRetriedQuarantine(merchantId: string, deliveryId: string): void {
    const key = `${merchantId}#${deliveryId}`
    this.stopRetryPolling(key)
    const startedAt = Date.now()
    const poll = async () => {
      if (this.destroyed) return this.stopRetryPolling(key)
      try {
        const item = await this.syncAuditService.getAtomicDelivery(merchantId, deliveryId, { signal: this.lifecycleAbort.signal })
        const terminal = item.status === 'ACKNOWLEDGED' || item.status === 'QUARANTINED'
        this.updateQuarantine(this.toQuarantineRow(item, !terminal, item.status === 'ACKNOWLEDGED' ? 'succeeded' : item.status === 'QUARANTINED' ? 'failed' : null))
        if (terminal) this.stopRetryPolling(key)
      } catch (error) {
        if (!this.destroyed) this.quarantinesError = error instanceof Error ? error.message : 'Failed to refresh delivery status.'
      }
      if (Date.now() - startedAt >= 60_000) {
        const row = this.quarantines.find((item) => this.quarantineKey(item) === key)
        if (row) this.updateQuarantine({ ...row, retrying: false })
        this.stopRetryPolling(key)
      }
    }
    void poll()
    this.retryTimers.set(key, setInterval(() => void poll(), 2_000))
  }

  private stopRetryPolling(key: string): void {
    const timer = this.retryTimers.get(key)
    if (timer) clearInterval(timer)
    this.retryTimers.delete(key)
  }

  private summaryToQuarantineRow(entry: SyncAuditEntry): SalesforceQuarantineRow | null {
    const details = entry.details && typeof entry.details === 'object' ? entry.details : {}
    const merchantId = typeof details.merchantId === 'string' ? details.merchantId : ''
    const deliveryId = typeof details.deliveryId === 'string' ? details.deliveryId : entry.id.replace('salesforce_atomic_delivery#', '')
    if (!merchantId || !deliveryId) return null
    return this.toQuarantineRow({
      merchantId, deliveryId, status: 'QUARANTINED', attemptCount: Number(entry.amount) || 0,
      manualRetryCount: typeof details.manualRetryCount === 'number' ? details.manualRetryCount : 0,
      entities: typeof details.terminalId === 'string' ? [{ type: 'terminal', id: details.terminalId }] : [],
      lastAttemptAt: typeof details.lastAttemptAt === 'string' ? details.lastAttemptAt : null,
      quarantinedAt: typeof details.quarantinedAt === 'string' ? details.quarantinedAt : null,
      lastError: typeof details.lastError === 'string' ? details.lastError : null,
    })
  }

  private toQuarantineRow(item: AtomicDelivery, retrying = false, result: SalesforceQuarantineRow['result'] = null): SalesforceQuarantineRow {
    return { ...item, terminalDisplay: item.entities[0]?.id || item.merchantId, retrying, result }
  }

  private quarantineKey(row: Pick<AtomicDelivery, 'merchantId' | 'deliveryId'>): string {
    return `${row.merchantId}#${row.deliveryId}`
  }

  private updateQuarantine(row: SalesforceQuarantineRow): void {
    const key = this.quarantineKey(row)
    const index = this.quarantines.findIndex((item) => this.quarantineKey(item) === key)
    this.quarantines = index < 0
      ? [...this.quarantines, row]
      : this.quarantines.map((item) => this.quarantineKey(item) === key ? row : item)
  }

  private syncMetricValues(): void {
    this.groups = this.groups.map((group) => ({
      ...group,
      baseline: group.baseline.map((metric) => ({
        ...metric,
        aws: this.auditEntries.get(metric.awsId) ?? null,
        salesforce: this.auditEntries.get(metric.salesforceId) ?? null,
      })),
      pending: group.pending.map((metric) => ({
        ...metric,
        entry: this.auditEntries.get(metric.id) ?? null,
      })),
    }))
  }

  formatValue(entry: SyncAuditEntry | null, format: SyncAuditMetricFormat): string {
    if (!entry || !Number.isFinite(Number(entry.amount))) return '—'
    return this.formatNumber(Number(entry.amount), format)
  }

  formatFriendlyName(entry: SyncAuditEntry | null): string {
    return entry?.friendly_name ?? 'Unavailable'
  }

  difference(metric: BaselineMetric): number | null {
    const aws = Number(metric.aws?.amount)
    const salesforce = Number(metric.salesforce?.amount)
    return Number.isFinite(aws) && Number.isFinite(salesforce) ? aws - salesforce : null
  }

  comparisonStatus(metric: BaselineMetric): 'match' | 'mismatch' | 'unavailable' {
    const difference = this.difference(metric)
    if (difference === null) return 'unavailable'
    return Math.abs(difference) < 0.005 ? 'match' : 'mismatch'
  }

  comparisonLabel(metric: BaselineMetric): string {
    const difference = this.difference(metric)
    if (difference === null) return 'Comparison unavailable'
    if (this.comparisonStatus(metric) === 'match') return 'Matched'
    return `AWS − Salesforce: ${this.formatNumber(difference, metric.format)}`
  }

  hasPendingCount(group: SyncAuditEntityGroup): boolean {
    return group.pending.some(
      (metric) => metric.format === 'number' && Number(metric.entry?.amount) !== 0,
    )
  }

  pendingForBaseline(
    group: SyncAuditEntityGroup,
    baseline: BaselineMetric,
  ): PendingMetric[] {
    if (group.title === 'Repayments') {
      return baseline.awsId === 'aws_repayments_count'
        ? group.pending.filter((metric) => metric.format === 'number')
        : group.pending.filter((metric) => metric.format === 'currency')
    }

    return group.title === 'Merchants' ? group.pending : []
  }

  showPendingForBaseline(
    group: SyncAuditEntityGroup,
    baseline: BaselineMetric,
  ): boolean {
    return (
      this.comparisonStatus(baseline) === 'mismatch' &&
      this.pendingForBaseline(group, baseline).some(
        (metric) => Number(metric.entry?.amount) !== 0,
      )
    )
  }

  pendingLabel(metric: PendingMetric): string {
    const friendlyName = this.formatFriendlyName(metric.entry)
    return metric.isDeletion ? `${friendlyName} (deletions)` : friendlyName
  }

  private formatNumber(amount: number, format: SyncAuditMetricFormat): string {
    if (format === 'number') {
      return new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 }).format(amount)
    }
    return new Intl.NumberFormat('en-GB', {
      style: 'currency', currency: 'GBP', minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(amount)
  }
}
